import React, { useState, useEffect, useMemo } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { github } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { atomOneLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { parse } from 'jsonc-parser';

import './ScriptPanel.css';

function formatExample(raw) {
  try {
    const text = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
    const hasComment = /(^|[\s{,])\/\/[^\n]*/.test(text);
    if (hasComment) return text;
    const parsed = parse(text);
    return JSON.stringify(parsed, null, 2);
  } catch (err) {
    console.error('🛑 Example JSONC parse failed:', err);
    return 'Invalid example format';
  }
}

export default function ScriptPanel({ schemaData }) {
  const [copiedSection, setCopiedSection] = useState(null);
  const [activeExampleIndex, setActiveExampleIndex] = useState(0);

  // Reset to first example whenever the schema changes
  useEffect(() => {
    setActiveExampleIndex(0);
  }, [schemaData]);

  // Normalize example(s) into an array of formatted strings
  const examples = useMemo(() => {
    const raw = schemaData?.example;
    if (!raw) return [];

    if (Array.isArray(raw)) {
      return raw.map((item) => formatExample(item)).filter(Boolean);
    }

    // Backward compat: single example
    const formatted = formatExample(raw);
    return formatted ? [formatted] : [];
  }, [schemaData?.example]);

  const handleCopy = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };
  if (!schemaData) return null;
  return (
    <div className="script-panel">
      {examples.length > 0 && (
        <>
          <div className="script-title" style={{ marginTop: '32px' }}>
            <strong>{examples.length > 1 ? 'Examples:' : 'Example:'}</strong>
          </div>

          <div className="code-block">
            <div className="script-header">
              <div className="script-tabs">
                {examples.length > 1 ? (
                  examples.map((_, index) => (
                    <button
                      key={index}
                      className={activeExampleIndex === index ? 'active' : ''}
                      onClick={() => setActiveExampleIndex(index)}
                    >
                      Example {index + 1}
                    </button>
                  ))
                ) : (
                  <button className="active">JSON</button>
                )}
              </div>
              <div className="copy-section">
                <button
                  className="copy-button"
                  onClick={() => handleCopy(examples[activeExampleIndex], 'example')}
                >
                  Copy code
                </button>
                {copiedSection === 'example' && <span className="copied-msg">Copied!</span>}
              </div>
            </div>

            <SyntaxHighlighter language="json" style={github}>
              {examples[activeExampleIndex] || '{}'}
            </SyntaxHighlighter>
          </div>
        </>
      )}
    </div>
  );
}
